import { useState, useMemo } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import {
  LayoutDashboard, Settings, Home, Users, Briefcase, Package, Image as ImageIcon,
  MessageSquare, LogOut, Info, KeyRound, Palette, FolderOpen, Shield, FileText,
  ChevronDown, Building2, Tag, Layers, Radio, ClipboardList, Sparkles, BarChart3,
  GitBranch, Megaphone, Sun, Moon, ListOrdered, Receipt, AlertTriangle, Calculator,
  History, MonitorPlay, Cloud,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

// ----- Sidebar groups -----
const GROUPS = [
  {
    key: "site",
    label: "Sitio público",
    icon: Home,
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, module: "dashboard" },
      { to: "/admin/settings", label: "Configuración", icon: Settings, module: "settings" },
      { to: "/admin/appearance", label: "Apariencia", icon: Palette, module: "appearance" },
      { to: "/admin/about", label: "Quiénes Somos", icon: Info, module: "about" },
      { to: "/admin/services", label: "Servicios (web)", icon: Briefcase, module: "services" },
      { to: "/admin/talents", label: "Talentos (web)", icon: Users, module: "talents" },
      { to: "/admin/products", label: "Productos (web)", icon: Package, module: "products" },
      { to: "/admin/clients", label: "Clientes (logos)", icon: ImageIcon, module: "clients" },
      { to: "/admin/media", label: "Medios", icon: FolderOpen, module: "media" },
    ],
  },
  {
    key: "commercial",
    label: "Comercial",
    icon: Sparkles,
    items: [
      { to: "/admin/comercial", label: "Dashboard Comercial", icon: Sparkles, end: true, module: "comercial" },
      { to: "/admin/comercial/clientes", label: "Clientes", icon: Building2, module: "com_clients" },
      { to: "/admin/comercial/marcas", label: "Marcas", icon: Tag, module: "com_brands" },
      { to: "/admin/comercial/agencias", label: "Agencias", icon: Briefcase, module: "com_agencies" },
      { to: "/admin/comercial/contactos", label: "Contactos / Ejecutivos", icon: Users, module: "com_contacts" },
      { to: "/admin/leads", label: "Contactos web (Leads)", icon: MessageSquare, module: "leads" },
      { to: "/admin/comercial/categorias", label: "Categorías", icon: Tag, module: "com_categories" },
      { to: "/admin/comercial/productos-com", label: "Productos comerciales", icon: Package, module: "com_products" },
      { to: "/admin/comercial/paquetes", label: "Paquetes", icon: Layers, module: "com_packages" },
      { to: "/admin/comercial/talentos", label: "Talentos comerciales", icon: Users, module: "com_talents" },
      { to: "/admin/proposals", label: "Propuestas", icon: FileText, module: "proposals" },
      { to: "/admin/proposal-themes", label: "Plantillas Propuesta", icon: Palette, module: "proposals" },
      { to: "/admin/comercial/workflow", label: "Workflow", icon: GitBranch, module: "com_workflow" },
      { to: "/admin/comercial/campanas", label: "Campañas", icon: Megaphone, module: "com_campaigns" },
    ],
  },
  {
    key: "accounting",
    label: "Contabilidad",
    icon: Calculator,
    items: [
      { to: "/admin/contabilidad", label: "Dashboard Contable", icon: Calculator, end: true, module: "com_invoices" },
      { to: "/admin/comercial/cobranza", label: "Órdenes de cobro", icon: Receipt, module: "com_invoices" },
      { to: "/admin/comercial/cuentas-por-cobrar", label: "Cuentas por cobrar", icon: AlertTriangle, module: "com_invoices" },
      { to: "/admin/comercial/reportes", label: "Reportes contables", icon: BarChart3, module: "com_reports" },
    ],
  },
  {
    key: "production",
    label: "Producción y Contenido",
    icon: MonitorPlay,
    items: [
      { to: "/admin/comercial/programas", label: "Programas streaming", icon: Radio, module: "programs" },
      { to: "/admin/produccion/acciones", label: "Acciones Comerciales", icon: ClipboardList, module: "programs" },
      { to: "/admin/contenido", label: "Contenido (herramientas)", icon: Cloud, module: "programs" },
    ],
  },
  {
    key: "system",
    label: "Sistema",
    icon: Shield,
    items: [
      { to: "/admin/historial", label: "Historial", icon: History, module: "settings" },
      { to: "/admin/users", label: "Usuarios", icon: Shield, module: "users" },
      { to: "/admin/menu-config", label: "Menú admin", icon: ListOrdered, module: "settings" },
      { to: "/admin/account", label: "Mi cuenta", icon: KeyRound, module: "account" },
    ],
  },
];

function applyOverrides(items, overrides) {
  if (!overrides) return items;
  return items
    .map((it, i) => {
      const ov = overrides[it.to] || {};
      return { ...it, label: ov.label || it.label, _order: ov.order ?? i, _hidden: !!ov.hidden };
    })
    .filter((it) => !it._hidden)
    .sort((a, b) => a._order - b._order);
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { config } = useSite();
  const overrides = config?.admin_menu_overrides || {};
  const nav = useNavigate();
  const loc = useLocation();
  const isSuper = user?.role === "super_admin";
  const allowed = new Set([...(user?.allowed_modules || []), "dashboard", "account"]);
  if (user?.role === "producer") {
    allowed.add("orders"); allowed.add("programs");
  }

  const filterNav = (items) => items.filter((n) => isSuper || allowed.has(n.module));

  const groups = useMemo(() => GROUPS.map((g) => ({
    ...g,
    items: applyOverrides(filterNav(g.items), overrides),
  })).filter((g) => g.items.length > 0), [overrides, user]); // eslint-disable-line

  const [openGroups, setOpenGroups] = useState(() => {
    const o = {};
    GROUPS.forEach((g) => {
      o[g.key] = (loc.pathname.startsWith(`/admin/${g.key}`) ||
                   g.items.some((it) => loc.pathname === it.to || loc.pathname.startsWith(it.to + "/")));
    });
    if (!Object.values(o).some(Boolean)) o.site = true;
    return o;
  });

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      <aside className="w-64 border-r border-border bg-secondary/20 flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-border">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm bg-primary" />
            <span className="font-display font-black text-lg tracking-tighter">INFLUCAST</span>
          </Link>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto" data-testid="sidebar-nav">
          {groups.map((g) => {
            const isOpen = !!openGroups[g.key];
            const Icon = g.icon;
            return (
              <div key={g.key} className="mb-1">
                <button
                  onClick={() => setOpenGroups((o) => ({ ...o, [g.key]: !o[g.key] }))}
                  data-testid={`group-toggle-${g.key}`}
                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-secondary/40 transition-colors"
                >
                  <span className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    <Icon className="w-3.5 h-3.5" />
                    {g.label}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`} />
                </button>
                {isOpen && (
                  <div className="px-2 pb-2">
                    {g.items.map((n) => <NavItem key={n.to} n={n} />)}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-border p-3 space-y-1">
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle"
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-sm hover:bg-secondary/40 transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === "dark" ? "Modo claro" : "Modo oscuro"}
          </button>
          <button
            onClick={() => { logout(); nav("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-sm hover:bg-destructive/10 hover:text-destructive transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({ n }) {
  const Icon = n.icon;
  return (
    <NavLink
      to={n.to}
      end={n.end}
      data-testid={`nav-${n.to.replace(/\//g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors ${
          isActive ? "bg-primary text-primary-foreground font-semibold" : "text-foreground hover:bg-secondary/40"
        }`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{n.label}</span>
    </NavLink>
  );
}
